import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { showToast } from '../components/Toast'
import {
  isCurrentUserAdmin,
  searchCustomers,
  createGuestProfile,
  adminCreateOrder,
  searchOrdersForPickup,
  attachOrderToPickup,
} from '../lib/admin'
import { listActiveServices } from '../lib/catalog'
import { computeReturn, allowedReturnSlots } from '../lib/orders'
import { availableSlotsFor, earliestPickupDate, pickupWindowFor, SLOTS } from '../lib/dates'
import { sendSms } from '../lib/sms'
import { sendBooked } from '../lib/emailTemplate'
import { findContactMatch, emailKey } from '../lib/contactMatch'

// /staff/orders/new — admin creates an order on a customer's behalf.
//
// Flow:
//   1. Search bar (debounced 300 ms) → matches from public.profiles.
//        A) admin clicks a row → the whole form is pre-filled, order will
//           be created under that user_id.
//        B) admin ignores matches → we still show a discreet warning at
//           save time and offer to reuse.
//        C) no match → guest path: a new profile is created (is_guest=true)
//           and the order is created under it.
//   2. Service picker (pulls from catalog).
//   3. Pickup date + slot, return date + slot (mirrors client flow).
//   4. Admin-only notes.
//   5. Create → redirects to the ticket in /staff so Sébastien continues
//      the workflow (weigh → send invoice → mark paid).
export default function StaffNewOrder() {
  const { user, loading: authLoading } = useAuth()
  const navigate = useNavigate()
  const [adminOk, setAdminOk] = useState(null)

  useEffect(() => {
    if (authLoading) return
    if (!user) { navigate('/login', { replace: true }); return }
    let alive = true
    ;(async () => {
      const ok = await isCurrentUserAdmin()
      if (!alive) return
      if (!ok) { navigate('/account', { replace: true }); return }
      setAdminOk(true)
    })()
    return () => { alive = false }
  }, [authLoading, user, navigate])

  // ── Search customer ───────────────────────────────────────────────
  const [query, setQuery] = useState('')
  const [results, setResults] = useState([])
  const [searching, setSearching] = useState(false)
  const [openList, setOpenList] = useState(false)
  const [pickedCustomer, setPickedCustomer] = useState(null) // null = guest path

  useEffect(() => {
    if (query.trim().length < 2) { setResults([]); return }
    setSearching(true)
    const t = setTimeout(async () => {
      try {
        const rows = await searchCustomers(query)
        setResults(rows)
        setOpenList(true)
      } catch (err) {
        showToast(err?.message || 'Search failed')
      } finally {
        setSearching(false)
      }
    }, 300)
    return () => clearTimeout(t)
  }, [query])

  function pickCustomer(row) {
    setPickedCustomer(row)
    setF((cur) => ({
      ...cur,
      first_name: row.first_name || '',
      last_name: row.last_name || '',
      email: row.email || '',
      phone: row.phone || '',
      street: row.address_line1 || '',
      city: row.city || '',
      state: row.state || '',
      zip: row.zip || '',
      hotelName: row.hotel_name || '',
      roomNumber: row.room_number || '',
    }))
    setQuery(customerLabel(row))
    setOpenList(false)
  }

  function clearPicked() {
    setPickedCustomer(null)
    setQuery('')
    setF(EMPTY_FORM)
  }

  // ── Form state ────────────────────────────────────────────────────
  const [f, setF] = useState(EMPTY_FORM)
  // Existing order this new one should ride along with (null = standalone).
  const [attachTo, setAttachTo] = useState(null)
  const set = (k, v) => setF((cur) => ({ ...cur, [k]: v }))

  // ── Catalog ───────────────────────────────────────────────────────
  const [services, setServices] = useState([])
  useEffect(() => {
    if (!adminOk) return
    listActiveServices().then(setServices).catch(() => {})
  }, [adminOk])
  const service = useMemo(
    () => services.find((s) => s.id === f.service_id) || null,
    [services, f.service_id],
  )

  // ── Dates + slots ─────────────────────────────────────────────────
  const earliest = useMemo(() => earliestPickupDate(), [])
  useEffect(() => {
    if (!f.pickupDate) set('pickupDate', earliest)
  }, [earliest]) // eslint-disable-line

  const pickupSlots = useMemo(() => availableSlotsFor(f.pickupDate), [f.pickupDate])
  useEffect(() => {
    if (f.pickupSlot && !pickupSlots.find((s) => s.id === f.pickupSlot)) {
      set('pickupSlot', '')
    }
  }, [f.pickupSlot, pickupSlots])

  // ── Same-Day window ───────────────────────────────────────────────
  // Same rule as the customer screen (pickupWindowFor), but here it WARNS
  // instead of blocking: staff sometimes have a reason. What they must not
  // be able to do is book it six weeks out without noticing — GL-1109 was
  // taken for a date 43 days away and nothing said a word.
  //
  // Two separate acknowledgements on purpose:
  //   windowLifted  — "I want to pick a date outside the window", removes
  //                   the input's max so the picker stops greying it out.
  //   outOfWindowOk — "yes, create it anyway", given after seeing the date.
  // The second resets whenever the date changes, so one confirmation can't
  // silently cover a different date later.
  const isSameDay = service?.key === 'same_day'
  const pickupWindow = useMemo(
    () => pickupWindowFor(isSameDay ? 'same_day' : null),
    [isSameDay],
  )
  const [windowLifted, setWindowLifted] = useState(false)
  const [outOfWindowOk, setOutOfWindowOk] = useState(false)
  useEffect(() => { setWindowLifted(false); setOutOfWindowOk(false) }, [service?.key])
  useEffect(() => { setOutOfWindowOk(false) }, [f.pickupDate])

  const outOfWindow = !!(
    isSameDay && f.pickupDate && pickupWindow.max && f.pickupDate > pickupWindow.max
  )

  const autoReturn = useMemo(
    () => computeReturn(service?.key, f.pickupDate, f.pickupSlot),
    [service?.key, f.pickupDate, f.pickupSlot],
  )
  useEffect(() => {
    if (!autoReturn) return
    if (autoReturn.deferred) {
      set('returnDate', '')
      set('returnSlot', '')
    } else {
      set('returnDate', autoReturn.date || '')
      set('returnSlot', autoReturn.slot || '')
    }
  }, [autoReturn]) // eslint-disable-line

  // Why `[]` is empty matters: it is NOT always "no slot that day". It can
  // also mean the service or the pickup time hasn't been chosen yet — and
  // the screen used to blame the date in all three cases, so the admin kept
  // changing the date, which was never the problem.
  const returnBlocker = !service
    ? 'Pick a service first'
    : !f.pickupDate || !f.pickupSlot
      ? 'Pick a pickup time first'
      : ''

  // allowedReturnSlots() returns KEYS ('morning' | 'evening'), not objects.
  // Rendering them straight gave buttons whose label, sub and id were all
  // undefined — two blank buttons that wrote returnSlot: undefined when
  // clicked. Map through SLOTS before rendering.
  const returnSlotChoices = useMemo(() => {
    if (returnBlocker || !f.returnDate) return []
    return (allowedReturnSlots(service.key, f.pickupDate, f.pickupSlot, f.returnDate) || [])
      .map((k) => SLOTS[k])
      .filter(Boolean)
  }, [returnBlocker, service, f.pickupDate, f.pickupSlot, f.returnDate])

  // ── Duplicate warning ─────────────────────────────────────────────
  // Fired when the admin ignores search results and types a raw email or
  // phone that already exists in a profile.
  //
  // The matching rule used to be written out here, and only here — which is
  // why the customer signup screen had no check at all. It now lives in
  // src/lib/contactMatch.js and both screens share it.
  //
  // Still limited to `results`: this only catches a duplicate the admin's
  // own search already surfaced. The customer side asks the server.
  const duplicate = useMemo(
    () => (pickedCustomer ? null : findContactMatch(results, { email: f.email, phone: f.phone })),
    [pickedCustomer, f.email, f.phone, results],
  )

  // ── Submit ────────────────────────────────────────────────────────
  const [submitting, setSubmitting] = useState(false)

  function validate() {
    if (!f.first_name.trim() && !f.last_name.trim()) return 'Customer name is required'
    if (!f.phone.trim()) return 'Phone is required'
    if (!f.street.trim() && !f.hotelName.trim()) return 'Address (or hotel) is required'
    if (!f.service_id) return 'Pick a service'
    if (!f.pickupDate) return 'Pickup date is required'
    // A warning, not a wall: it clears as soon as the staff confirms.
    if (outOfWindow && !outOfWindowOk) return 'Confirm the Same-Day date first'
    if (!f.pickupSlot) return 'Pickup slot is required'
    // A return date with no slot used to be possible: the blank buttons
    // wrote `undefined` and it was sent straight through. The driver then
    // had a delivery day and no window.
    if (!autoReturn?.deferred && f.returnDate && !f.returnSlot) {
      return 'Pick a return time window'
    }
    return null
  }

  // Same rule, surfaced before the click so the button says why it's off.
  const blockReason = validate()

  async function handleCreate() {
    const err = validate()
    if (err) { showToast(err); return }
    setSubmitting(true)
    try {
      // Resolve the user_id — existing customer OR new guest profile.
      let userId
      if (pickedCustomer) {
        userId = pickedCustomer.id
      } else {
        const guest = await createGuestProfile({
          first_name: f.first_name.trim() || null,
          last_name: f.last_name.trim() || null,
          email: f.email.trim() || null,
          phone: f.phone.trim() || null,
          address_line1: f.street.trim() || null,
          address_line2: f.apt.trim() || null,
          city: f.city.trim() || null,
          state: f.state.trim() || null,
          zip: f.zip.trim() || null,
          hotel_name: f.hotelName.trim() || null,
          room_number: f.roomNumber.trim() || null,
        })
        userId = guest.id
      }

      const order = await adminCreateOrder({
        user_id: userId,
        service: service?.key,
        service_id: f.service_id,
        bags: f.bags || '1',
        pickupDate: f.pickupDate,
        pickupSlot: f.pickupSlot,
        returnDate: autoReturn?.deferred ? null : f.returnDate || null,
        returnSlot: autoReturn?.deferred ? null : f.returnSlot || null,
        returnDeferred: !!autoReturn?.deferred,
        address: {
          street: f.street.trim(),
          apt: f.apt.trim(),
          city: f.city.trim(),
          state: f.state.trim(),
          zip: f.zip.trim(),
          hotelName: f.hotelName.trim(),
          roomNumber: f.roomNumber.trim(),
        },
        notes: f.notes.trim(),
      })

      // Join the existing pickup, if staff picked one. Done server-side so
      // the group is created atomically and the whole group flips to
      // "return TBD" — the other order's service is never touched.
      if (attachTo?.id) {
        try {
          await attachOrderToPickup(order.id, attachTo.id)
          showToast(`Added to the pickup with ${attachTo.order_number}`)
        } catch (err) {
          showToast(err?.message || 'Order created, but joining the pickup failed')
        }
      }

      // Confirmation email + "pickup confirmed" SMS. Awaited so the
      // navigate() below doesn't abort the fetch.
      //
      // When this order JOINED an existing pickup, the Edge Function skips
      // the email on its own: only the first order of a group sends one, so
      // the customer never gets a second confirmation for the same stop.
      try { await sendBooked(order.id) } catch { /* ignore */ }
      try { await sendSms(order.id, 'pickup_confirmed') } catch { /* ignore */ }

      showToast(`Order created for ${displayName(f) || 'customer'}`)
      navigate(`/staff/orders/${encodeURIComponent(order.order_number)}`, { replace: true })
    } catch (err) {
      showToast(err?.message || 'Create order failed')
    } finally {
      setSubmitting(false)
    }
  }

  if (adminOk === null) {
    return <Splash />
  }

  return (
    <div className="min-h-screen bg-[#F9FAFB] pb-24">
      <header className="border-b border-line bg-white px-4 py-3">
        <div className="mx-auto flex max-w-3xl items-center justify-between">
          <button
            type="button"
            onClick={() => navigate('/staff')}
            className="text-[13px] font-bold text-navy"
          >
            ← Back to tickets
          </button>
          <div className="font-bricolage text-[15px] font-extrabold text-navy">
            + New Order
          </div>
          <div className="w-24" />
        </div>
      </header>

      <div className="mx-auto max-w-3xl px-4 py-6 space-y-6">
        {/* 1. Search customer */}
        <Section
          number="1"
          title="Customer"
          subtitle="Search existing profiles — name, phone, or email. Skip to create a guest."
        >
          <div className="relative">
            <input
              type="text"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value)
                if (pickedCustomer) setPickedCustomer(null)
              }}
              placeholder="Search customer…"
              className="field-input pr-24"
            />
            {(pickedCustomer || query) && (
              <button
                type="button"
                onClick={clearPicked}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-[11px] font-bold text-ink-3 hover:text-red"
              >
                Clear
              </button>
            )}
            {openList && results.length > 0 && !pickedCustomer && (
              <ul className="absolute z-10 mt-1 max-h-72 w-full overflow-y-auto rounded-lg border border-line bg-white shadow-lg">
                {results.map((r) => (
                  <li
                    key={r.id}
                    onClick={() => pickCustomer(r)}
                    className="flex cursor-pointer items-center justify-between gap-2 border-b border-line px-3 py-2 text-[13px] hover:bg-[#F9FAFB] last:border-b-0"
                  >
                    <div className="min-w-0">
                      <div className="truncate font-bold text-navy">
                        {r.member_status === 'active' && <span className="mr-1">🏆</span>}
                        {r.is_guest && <span className="mr-1">👻</span>}
                        {customerLabel(r)}
                      </div>
                      <div className="truncate text-[11px] text-ink-2">
                        {r.email || '—'} · {r.phone || '—'}
                      </div>
                    </div>
                    <span className="text-[11px] font-extrabold text-aqua">Use</span>
                  </li>
                ))}
              </ul>
            )}
            {searching && (
              <div className="mt-1 text-[11px] text-ink-3">Searching…</div>
            )}
          </div>

          {pickedCustomer && (
            <div className="mt-3 rounded-lg border border-aqua bg-aqua/5 p-3 text-[12px]">
              <div className="font-bold text-navy">
                {pickedCustomer.member_status === 'active' && '🏆 '}
                {customerLabel(pickedCustomer)}
              </div>
              <div className="mt-1 text-ink-2">
                Order will be created under this profile.
              </div>
            </div>
          )}

          {duplicate && !pickedCustomer && (
            <div className="mt-3 flex items-center justify-between gap-3 rounded-lg border border-gold bg-gold-soft p-3 text-[12px]">
              <span className="text-navy">
                <b>{customerLabel(duplicate)}</b> already has a profile with this{' '}
                {emailKey(f.email) && emailKey(duplicate.email) === emailKey(f.email)
                  ? 'email'
                  : 'phone'}.
              </span>
              <button
                type="button"
                onClick={() => pickCustomer(duplicate)}
                className="shrink-0 rounded-md bg-navy px-3 py-1 text-[11px] font-extrabold text-white"
              >
                Use this profile
              </button>
            </div>
          )}
        </Section>

        {/* 2. Contact + address */}
        <Section number="2" title="Details" subtitle="Prefilled if a customer was selected.">
          <div className="grid grid-cols-2 gap-2">
            <input className="field-input" placeholder="First name" value={f.first_name} onChange={(e) => set('first_name', e.target.value)} />
            <input className="field-input" placeholder="Last name" value={f.last_name} onChange={(e) => set('last_name', e.target.value)} />
          </div>
          <input className="field-input mt-2" placeholder="Phone (required)" value={f.phone} onChange={(e) => set('phone', e.target.value)} />
          <input className="field-input mt-2" placeholder="Email (optional — needed for Stripe Invoice)" value={f.email} onChange={(e) => set('email', e.target.value)} />
          <input className="field-input mt-2" placeholder="Street address" value={f.street} onChange={(e) => set('street', e.target.value)} />
          <input className="field-input mt-2" placeholder="Apt / Suite (optional)" value={f.apt} onChange={(e) => set('apt', e.target.value)} />
          <div className="mt-2 grid grid-cols-2 gap-2">
            <input className="field-input" placeholder="City" value={f.city} onChange={(e) => set('city', e.target.value)} />
            <input className="field-input" placeholder="State" value={f.state} onChange={(e) => set('state', e.target.value)} />
          </div>
          <input className="field-input mt-2" placeholder="ZIP" value={f.zip} onChange={(e) => set('zip', e.target.value)} />
          <div className="mt-2 grid grid-cols-2 gap-2">
            <input className="field-input" placeholder="Hotel (if any)" value={f.hotelName} onChange={(e) => set('hotelName', e.target.value)} />
            <input className="field-input" placeholder="Room #" value={f.roomNumber} onChange={(e) => set('roomNumber', e.target.value)} />
          </div>
        </Section>

        {/* 3. Service */}
        <Section number="3" title="Service" subtitle="Pricing is applied at weigh-in — same as regular orders.">
          <select
            className="field-input"
            value={f.service_id}
            onChange={(e) => set('service_id', e.target.value)}
          >
            <option value="">— Pick a service —</option>
            {services.map((s) => (
              <option key={s.id} value={s.id}>{s.name || s.key}</option>
            ))}
          </select>
          <div className="mt-2 grid grid-cols-2 gap-2">
            <input
              className="field-input"
              placeholder="Bags"
              type="number"
              min="1"
              max="10"
              value={f.bags}
              onChange={(e) => set('bags', e.target.value)}
            />
          </div>
        </Section>

        {/* 4. Pickup */}
        <Section number="4" title="Pickup">
          <input
            type="date"
            className="field-input"
            value={f.pickupDate}
            onChange={(e) => set('pickupDate', e.target.value)}
            min={isSameDay ? pickupWindow.min : earliest}
            max={isSameDay && !windowLifted ? pickupWindow.max : undefined}
          />

          {isSameDay && !outOfWindow && (
            <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-ink-2">
              <span>
                Same-Day is a morning pickup returned that evening — normally{' '}
                {pickupWindow.min === pickupWindow.max ? 'tomorrow only' : 'today or tomorrow'}.
              </span>
              {!windowLifted && (
                <button
                  type="button"
                  onClick={() => setWindowLifted(true)}
                  className="font-bold text-aqua underline"
                >
                  Need another date?
                </button>
              )}
            </div>
          )}

          {outOfWindow && (
            <div
              className="mt-2 rounded-[10px] p-3 text-[12px]"
              style={{ background: '#FEF9E7', border: '1px solid #F0DCA0', color: '#7A5E0C' }}
            >
              <div className="font-bricolage text-[13px] font-extrabold">
                Same-Day outside its window — is that intended?
              </div>
              <p className="mt-1 leading-relaxed">
                You picked {f.pickupDate}, which is past {pickupWindow.max}. A Same-Day
                is collected in the morning and returned the same evening, so the
                customer would be waiting until then. Wash &amp; Fold is the service
                for a later date.
              </p>
              {outOfWindowOk ? (
                <div className="mt-2 font-bold">Confirmed — you can create the order.</div>
              ) : (
                <button
                  type="button"
                  onClick={() => setOutOfWindowOk(true)}
                  className="mt-2 rounded-md bg-navy px-3 py-1.5 text-[11px] font-extrabold text-white"
                >
                  Yes, create it anyway
                </button>
              )}
            </div>
          )}
          <div className="mt-2 grid grid-cols-2 gap-2">
            {pickupSlots.length === 0 && (
              <div className="col-span-2 text-[12px] text-ink-3">
                No slots available for that date — pick a later day.
              </div>
            )}
            {pickupSlots.map((slot) => (
              <button
                key={slot.id}
                type="button"
                onClick={() => set('pickupSlot', slot.id)}
                className={`rounded-lg border px-3 py-2 text-[12px] font-bold ${
                  f.pickupSlot === slot.id
                    ? 'border-navy bg-navy text-white'
                    : 'border-line bg-white text-navy'
                }`}
              >
                {slot.title} · {slot.sub}
              </button>
            ))}
          </div>
        </Section>

        {/* 5. Return */}
        <Section
          number="5"
          title="Return"
          subtitle={autoReturn?.deferred ? 'Confirmed after pickup — no date needed now.' : 'Auto-picked from pickup; you can override.'}
        >
          {autoReturn?.deferred ? (
            <div className="rounded-lg border border-line bg-white px-3 py-2 text-[12px] text-ink-2">
              TBD — will be confirmed after pickup.
            </div>
          ) : (
            <>
              <input
                type="date"
                className="field-input"
                value={f.returnDate}
                onChange={(e) => set('returnDate', e.target.value)}
                min={f.pickupDate || earliest}
              />
              <div className="mt-2 grid grid-cols-2 gap-2">
                {returnSlotChoices.map((slot) => (
                  <button
                    key={slot.id}
                    type="button"
                    onClick={() => set('returnSlot', slot.id)}
                    className={`rounded-lg border px-3 py-2 text-[12px] font-bold ${
                      f.returnSlot === slot.id
                        ? 'border-navy bg-navy text-white'
                        : 'border-line bg-white text-navy'
                    }`}
                  >
                    {slot.title} · {slot.sub}
                  </button>
                ))}
                {returnSlotChoices.length === 0 && (returnBlocker || f.returnDate) && (
                  <div className="col-span-2 text-[12px] text-ink-3">
                    {/* Say what is actually missing. Blaming the date sent
                        the admin round in circles changing a date that was
                        never the problem. */}
                    {returnBlocker || 'No return slot available on that date.'}
                  </div>
                )}
              </div>
            </>
          )}
        </Section>

        {/* 6. Notes */}
        <Section number="6" title="Admin notes" subtitle="Only visible to staff.">
          <textarea
            className="field-input min-h-[80px]"
            value={f.notes}
            onChange={(e) => set('notes', e.target.value)}
            placeholder="Free text — special instructions, source (WhatsApp / call), etc."
          />
        </Section>

        {/* 7. Join an existing pickup — never overwrite another order's
            service; a second service is always a second order. */}
        <Section
          number="7"
          title="Add to existing pickup"
          subtitle="Optional — same stop, second service. Everything then returns together."
        >
          <AttachPickup value={attachTo} onChange={setAttachTo} />
        </Section>
      </div>

      {/* Sticky footer */}
      <footer className="fixed inset-x-0 bottom-0 z-20 border-t border-line bg-white p-3">
        <div className="mx-auto flex max-w-3xl items-center justify-between gap-3">
          <div className="text-[11px] text-ink-2">
            {pickedCustomer
              ? <>Under <b className="text-navy">{customerLabel(pickedCustomer)}</b></>
              : 'New guest profile will be created'}
          </div>
          <div className="flex flex-col items-end gap-1">
            {blockReason && !submitting && (
              <span className="text-[11px] font-bold text-ink-3">{blockReason}</span>
            )}
            <button
              type="button"
              onClick={handleCreate}
              disabled={submitting || !!blockReason}
              title={blockReason || undefined}
              className={`rounded-xl bg-navy px-5 py-3 font-bricolage text-[13px] font-extrabold text-white ${
                submitting || blockReason ? 'opacity-50' : ''
              }`}
            >
              {submitting ? 'Creating…' : 'Create order'}
            </button>
          </div>
        </div>
      </footer>
    </div>
  )
}

// ── helpers ─────────────────────────────────────────────────────────

const EMPTY_FORM = {
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  street: '',
  apt: '',
  city: '',
  state: '',
  zip: '',
  hotelName: '',
  roomNumber: '',
  service_id: '',
  bags: '1',
  pickupDate: '',
  pickupSlot: '',
  returnDate: '',
  returnSlot: '',
  notes: '',
}

function customerLabel(r) {
  return [r.first_name, r.last_name].filter(Boolean).join(' ').trim() || r.email || r.phone || 'Unnamed'
}
function displayName(f) {
  return [f.first_name, f.last_name].filter(Boolean).join(' ').trim()
}

function Section({ number, title, subtitle, children }) {
  return (
    <section className="rounded-xl border border-line bg-white p-4">
      <div className="mb-2 flex items-baseline gap-2">
        <span className="text-[10px] font-extrabold uppercase tracking-widest text-ink-3">
          Step {number}
        </span>
        <h2 className="font-bricolage text-[14px] font-extrabold text-navy">{title}</h2>
      </div>
      {subtitle && <p className="mb-3 text-[11px] text-ink-2">{subtitle}</p>}
      {children}
    </section>
  )
}

// Search an open order by number or customer and ride along with it.
function AttachPickup({ value, onChange }) {
  const [term, setTerm] = useState('')
  const [rows, setRows] = useState([])
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (value) return
    const t = String(term || '').trim()
    if (t.length < 2) { setRows([]); return }
    let alive = true
    const timer = setTimeout(async () => {
      setBusy(true)
      try {
        const res = await searchOrdersForPickup(t)
        if (alive) setRows(res)
      } catch { if (alive) setRows([]) } finally { if (alive) setBusy(false) }
    }, 250)
    return () => { alive = false; clearTimeout(timer) }
  }, [term, value])

  if (value) {
    return (
      <div className="flex items-center justify-between gap-3 rounded-lg border border-aqua bg-aqua-soft px-3 py-2">
        <div className="min-w-0 text-[12px] text-navy">
          Joining <b>#{value.order_number}</b> · {value.customer_name || '—'}
          <div className="text-[10px] text-navy/70">
            Both orders will return together (date confirmed when everything is ready).
          </div>
        </div>
        <button
          type="button"
          onClick={() => { onChange(null); setTerm('') }}
          className="shrink-0 rounded-md border border-line bg-white px-2 py-1 text-[11px] font-extrabold text-red"
        >
          Remove
        </button>
      </div>
    )
  }

  return (
    <div>
      <input
        className="field-input"
        value={term}
        onChange={(e) => setTerm(e.target.value)}
        placeholder="Order number or customer name — leave empty for a standalone order"
      />
      {busy && <p className="mt-1 text-[11px] text-ink-3">Searching…</p>}
      {rows.length > 0 && (
        <ul className="mt-2 divide-y divide-line overflow-hidden rounded-lg border border-line">
          {rows.map((r) => (
            <li key={r.id}>
              <button
                type="button"
                onClick={() => onChange(r)}
                className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-[12px] hover:bg-[#F9FAFB]"
              >
                <span className="min-w-0">
                  <b className="text-navy">#{r.order_number}</b>{' '}
                  <span className="text-ink-2">{r.customer_name || '—'}</span>
                  <span className="block text-[10px] text-ink-3">
                    {r.service_name || r.service} · {r.status}
                  </span>
                </span>
                <span aria-hidden className="text-ink-3">›</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function Splash() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-white">
      <div className="text-[13px] font-bold text-ink-2">Loading…</div>
    </div>
  )
}
